/**
 * Stoic Training Cards — central registry.
 *
 * Each card is a static 1080×1080 PNG living under /public/cards/.
 * Adding a new card = drop the PNG into /public/cards/ and append an
 * entry to CARDS below. The grid, filters, modal, and share actions all
 * pick it up automatically.
 *
 * Field reference:
 *   slug         — URL-safe id (also used for the share permalink)
 *   filename     — file under /public/cards/ (without leading slash)
 *   category     — one of CATEGORY_SLUGS
 *   theme        — small uppercase label shown under the tile, e.g. "Wisdom"
 *   attribution  — author/source shown on the tile
 *   quote        — bilingual quote text (only used for SEO + alt text)
 *   bgColor      — fallback color while the PNG loads (matches the PNG)
 *
 * `quote` is `{ en, nl }` — the PNG already has the English quote baked in.
 * The Dutch quote here is used for alt text + meta description when the
 * user has NL selected. (Future: ship localized PNG variants.)
 */

export const CATEGORY_SLUGS = ["all", "virtue", "emotion", "philosophy"];

// i18n keys live in translations.js under cards.cat.*
export const CATEGORY_LABEL_KEYS = {
  all: "cards.cat.all",
  virtue: "cards.cat.virtue",
  emotion: "cards.cat.emotion",
  philosophy: "cards.cat.philosophy",
};

export const CARDS = [
  {
    slug: "wisdom-marcus-power-of-mind",
    filename: "v01-wisdom-marcus.png",
    category: "virtue",
    theme: { en: "Wisdom", nl: "Wijsheid" },
    attribution: "Marcus Aurelius",
    quote: {
      en: "You have power over your mind, not outside events. Realize this, and you will find strength.",
      nl: "Je hebt macht over je geest, niet over uiterlijke gebeurtenissen. Besef dit, en je zult kracht vinden.",
    },
    bgColor: "#070605",
  },
  {
    slug: "courage-seneca-to-live",
    filename: "v02-courage-seneca.png",
    category: "virtue",
    theme: { en: "Courage", nl: "Moed" },
    attribution: "Seneca",
    quote: {
      en: "Sometimes even to live is an act of courage.",
      nl: "Soms is zelfs leven een daad van moed.",
    },
    bgColor: "#070605",
  },
  {
    slug: "temperance-epictetus-few-wants",
    filename: "v03-temperance-epictetus.png",
    category: "virtue",
    theme: { en: "Temperance", nl: "Matigheid" },
    attribution: "Epictetus",
    quote: {
      en: "Wealth consists not in having great possessions, but in having few wants.",
      nl: "Rijkdom bestaat niet in het hebben van grote bezittingen, maar in het hebben van weinig verlangens.",
    },
    bgColor: "#070605",
  },
  {
    slug: "anger-marcus-best-revenge",
    filename: "e01-anger-marcus.png",
    category: "emotion",
    theme: { en: "Anger", nl: "Woede" },
    attribution: "Marcus Aurelius",
    quote: {
      en: "The best revenge is to be unlike him who performed the injury.",
      nl: "De beste wraak is anders te zijn dan degene die het onrecht beging.",
    },
    bgColor: "#070605",
  },
  {
    slug: "fear-seneca-imagination",
    filename: "e02-fear-seneca.png",
    category: "emotion",
    theme: { en: "Fear", nl: "Angst" },
    attribution: "Seneca",
    quote: {
      en: "We suffer more often in imagination than in reality.",
      nl: "We lijden vaker in onze verbeelding dan in werkelijkheid.",
    },
    bgColor: "#070605",
  },
  {
    slug: "logos-marcus-universe-is-change",
    filename: "p01-logos-marcus.png",
    category: "philosophy",
    theme: { en: "Logos", nl: "Logos" },
    attribution: "Marcus Aurelius",
    quote: {
      en: "The universe is change; our life is what our thoughts make it.",
      nl: "Het universum is verandering; ons leven is wat onze gedachten ervan maken.",
    },
    bgColor: "#070605",
  },
];

export function findCardBySlug(slug) {
  return CARDS.find((c) => c.slug === slug) || null;
}

export function filteredCards(filter = "all") {
  if (filter === "all") return CARDS.slice();
  return CARDS.filter((c) => c.category === filter);
}

export function cardPublicUrl(card) {
  return `/cards/${card.filename}`;
}

export function cardShareUrl(card) {
  // Public share URL (the cards page deep-linked to the card).
  return `https://stoicos.ai/stoic-training-cards/${card.slug}`;
}
